import type { EtapaFunilSeo } from "@ida/core";

export interface SecaoArtigoBlog {
  titulo: string;
  paragrafos: string[];
  itens?: string[];
  paragrafosAposItens?: string[];
  links?: Array<{ rotulo: string; href: string }>;
}

export interface ArtigoBlog {
  id: string;
  slug: string;
  titulo: string;
  descricao: string;
  resumo: string;
  categoria: string;
  etapaFunil: EtapaFunilSeo;
  intencao: string;
  palavrasChave: string[];
  publicadoEm: string;
  revisadoEm: string;
  tempoLeitura: string;
  autor: { nome: string; cargo: string };
  revisor: { nome: string; cargo: string };
  imagem: { src: string; largura: number; altura: number; alt: string };
  imagemInterna?: { src: string; largura: number; altura: number; alt: string; aposSecao: number; legenda?: string };
  secoes: SecaoArtigoBlog[];
  chamada: { titulo: string; texto: string; rotulo: string; href: string };
}

export const ARTIGOS_BLOG: ArtigoBlog[] = [
{
  "id": "seo-daily-2026-09-11_demonstrativo-repasse",
  "slug": "como-conferir-demonstrativo-repasse-imobiliaria",
  "titulo": "Como conferir o demonstrativo de repasse da imobiliária",
  "descricao": "Confira datas, descontos e comprovantes do repasse da imobiliária, com exemplo de cálculo e uma mensagem para esclarecer diferenças.",
  "resumo": "O valor que a imobiliária depositou ficou abaixo do aluguel do contrato. Antes de concluir que houve erro, vale reconstruir o caminho do dinheiro: quanto o inquilino pagou, quais lançamentos foram descontados e quanto efetivamente chegou à sua conta.\n\nEste roteiro propõe uma conferência do demonstrativo de repasse: reconstruir a conta, localizar diferenças e pedir os documentos que faltam.",
  "categoria": "Gestão do aluguel",
  "etapaFunil": "consideracao",
  "intencao": "Conferir aluguel bruto, descontos documentados, datas e repasse líquido sem confundir caixa e informação tributária.",
  "palavrasChave": [
    "demonstrativo de repasse",
    "repasse da imobiliária"
  ],
  "publicadoEm": "2026-09-15",
  "revisadoEm": "2026-09-15",
  "tempoLeitura": "4 min",
  "autor": {
    "nome": "Equipe editorial Alugando.com",
    "cargo": "Redação editorial"
  },
  "revisor": {
    "nome": "Alugando.com",
    "cargo": "Revisão automatizada de fontes"
  },
  "imagem": {
    "src": "/midia/seo/img_1789124939883_2ef9df6b.webp",
    "largura": 1536,
    "altura": 1024,
    "alt": "Casa marfim, documentos sem escrita e lupa azul representam a conferência do repasse."
  },
  "imagemInterna": {
    "src": "/midia/seo/img_1789125246159_2fa693a9.webp",
    "largura": 1536,
    "altura": 1024,
    "alt": "Três grupos de documentos representam a separação entre créditos, descontos e saldo.",
    "aposSecao": 2,
    "legenda": "Três grupos de documentos representam a separação entre créditos, descontos e saldo."
  },
  "secoes": [
    {
      "titulo": "1. Separe os documentos da mesma locação",
      "paragrafos": [
        "Nossa sugestão é começar com cinco itens: contrato de locação e aditivos, contrato de administração, demonstrativo do período, comprovantes dos lançamentos e extrato do depósito recebido. Identifique imóvel e contrato em todos eles para evitar misturar repasses de unidades diferentes.",
        "Monte uma linha para cada lançamento, com descrição, data, valor e documento correspondente. Se um único depósito reunir dois imóveis, peça a discriminação antes de dividir o total por conta própria. Se houve pagamento parcial, deixe a parcela ainda não recebida separada do valor já pago.",
        "Esse é um método de organização, não um modelo obrigatório de documento. Você pode usá-lo numa planilha ou no controle mensal da locação. O importante é conseguir voltar da soma ao comprovante que explica cada parcela."
      ],
      "links": []
    },
    {
      "titulo": "2. Reconstrua a conta do repasse",
      "paragrafos": [
        "Como roteiro de conciliação, some os créditos destinados ao proprietário e subtraia os descontos documentados do período. Depois, compare o resultado com a transferência efetiva. Se houver saldo anterior ou adiantamento, mostre-o em linha própria.",
        "Considere este exemplo fictício de um contrato cuja taxa de administração é de 8% exclusivamente sobre o aluguel recebido. Não há saldo anterior, outros encargos, reembolso ou retenção tributária no exemplo:"
      ],
      "paragrafosAposItens": [
        "Se o extrato mostrar R$ 2.560,00, há R$ 50,00 a esclarecer. Esse resultado identifica uma diferença; sozinho, não demonstra cobrança indevida. Pode haver outro lançamento a apresentar, uma compensação ou um erro que precise de correção.",
        "Os 8% são apenas uma hipótese aritmética. Confira a taxa e sua base no seu contrato, inclusive se houve cobrança de intermediação. A Receita descreve a comissão de administração como o valor pago pelo locador à imobiliária conforme o contrato."
      ],
      "itens": [
        "Aluguel recebido: R$ 3.000,00.",
        "Taxa de administração: R$ 3.000,00 × 8% = R$ 240,00.",
        "Serviço autorizado pelo proprietário e comprovado: R$ 150,00.",
        "Repasse esperado: R$ 3.000,00 − R$ 240,00 − R$ 150,00 = R$ 2.610,00."
      ],
      "links": [
        {
          "rotulo": "Receita Federal: dimob",
          "href": "https://www.gov.br/receitafederal/pt-br/assuntos/orientacao-tributaria/declaracoes-e-demonstrativos/dimob/perguntas-e-respostas"
        }
      ]
    },
    {
      "titulo": "3. Peça o documento por trás de cada desconto",
      "paragrafos": [
        "Para cada despesa, nossa recomendação é conferir descrição, responsável pelo pagamento, autorização quando aplicável e comprovante. Um texto genérico como “ajuste” não ajuda a reconstituir a conta: peça a origem, o período e a forma de cálculo.",
        "Se aparecer manutenção, procure a identificação do serviço e o documento correspondente. Se houver reembolso, confira a saída original e a devolução para não contar o mesmo valor duas vezes. Se constar saldo de mês anterior, compare com o demonstrativo anterior antes de encerrar a conferência.",
        "Guarde também a versão corrigida do demonstrativo. Manter a primeira versão junto da correção permite entender o que mudou e evita que uma divergência reapareça no mês seguinte."
      ],
      "links": []
    },
    {
      "titulo": "4. Registre a divergência de forma objetiva",
      "paragrafos": [
        "Você pode usar esta mensagem como ponto de partida, preenchendo apenas os dados necessários:",
        "“Estou conferindo o repasse do contrato [identificação], referente a [período]. Pelos lançamentos apresentados, cheguei a R$ [total], mas o crédito recebido foi de R$ [depósito]. Podem detalhar a diferença de R$ [diferença] e enviar os comprovantes dos ajustes? Se houver correção, peço o demonstrativo atualizado.”",
        "Registre a resposta e marque a pendência como resolvida somente depois de conferir a explicação ou o crédito complementar. Uma rotina mensal fica mais útil quando mostra tanto o que fechou quanto o que ainda depende de retorno.",
        "A conferência termina quando o valor fecha e cada ajuste tem uma explicação verificável. Este roteiro não calcula imposto: para essa etapa, consulte os guias sobre mês de apuração e despesas que podem reduzir o aluguel tributável.",
        "Para organizar esse histórico, conheça o Alugando.com: reúna recebimentos e documentos da locação numa rotina que facilite a conferência de cada mês."
      ],
      "links": [
        {
          "rotulo": "mês de apuração",
          "href": "/blog/aluguel-recebido-atraso-mes-imposto/"
        },
        {
          "rotulo": "despesas que podem reduzir o aluguel tributável",
          "href": "/blog/iptu-condominio-taxa-imobiliaria-aluguel-tributavel/"
        },
        {
          "rotulo": "Alugando.com",
          "href": "/"
        }
      ]
    }
  ],
  "chamada": {
    "titulo": "Organize a rotina do aluguel",
    "texto": "Organize recebimentos e documentos da locação com o Alugando.com.",
    "rotulo": "Conhecer o Alugando.com",
    "href": "/#como-funciona"
  }
},
{
  "id": "seo-daily-2026-09-13_vistoria-entrada-saida",
  "slug": "vistoria-entrada-saida-checklist-proprietario",
  "titulo": "Vistoria de entrada e saída: checklist para o proprietário",
  "descricao": "Checklist de vistoria de entrada e saída para organizar fotos, conferir ambientes e registrar diferenças sem confundir desgaste normal com dano.",
  "resumo": "Uma foto da sala inteira ajuda a reconhecer o ambiente, mas talvez não mostre o trinco que não fecha ou a peça lascada junto à porta. Para conferir o imóvel na devolução, vale preparar desde a entrada um registro que permita comparar cada item.\n\nEste checklist é uma sugestão de organização para proprietários de imóveis urbanos alugados. Ele reúne descrição, fotos e pendências; não substitui avaliação técnica nem decide sozinho quem deve pagar um reparo.",
  "categoria": "Gestão do aluguel",
  "etapaFunil": "consideracao",
  "intencao": "Comparar o estado do imóvel com descrição por ambiente, fotos identificadas e registro de divergências.",
  "palavrasChave": [
    "demonstrativo de repasse",
    "repasse da imobiliária"
  ],
  "publicadoEm": "2026-09-15",
  "revisadoEm": "2026-09-15",
  "tempoLeitura": "4 min",
  "autor": {
    "nome": "Equipe editorial Alugando.com",
    "cargo": "Redação editorial"
  },
  "revisor": {
    "nome": "Alugando.com",
    "cargo": "Revisão automatizada de fontes"
  },
  "imagem": {
    "src": "/midia/seo/img_1789297690503_3241e0f6.webp",
    "largura": 1536,
    "altura": 1024,
    "alt": "Cômodo marfim com porta, janela e câmera azul representa o registro do estado do imóvel."
  },
  "imagemInterna": {
    "src": "/midia/seo/img_1789297772279_e267ec19.webp",
    "largura": 1536,
    "altura": 1024,
    "alt": "Duas molduras com o mesmo detalhe de porta e uma lupa representam a comparação das vistorias.",
    "aposSecao": 2,
    "legenda": "Duas molduras com o mesmo detalhe de porta e uma lupa representam a comparação das vistorias."
  },
  "secoes": [
    {
      "titulo": "1. Combine a visita e identifique o registro",
      "paragrafos": [
        "Na locação urbana, o locatário deve permitir a vistoria pelo locador ou representante mediante combinação prévia de dia e hora. O locador deve fornecer, se solicitado, descrição detalhada do estado do imóvel na entrega, incluindo defeitos existentes.",
        "Nossa sugestão é começar o documento com endereço ou identificação do imóvel, data, participantes e indicação de entrada ou saída. Separe o contrato, o registro anterior e as observações enviadas pelas partes. Combine também como cada pessoa receberá uma cópia e apontará divergências.",
        "Evite escrever apenas “imóvel em bom estado”. Percorra os ambientes sempre na mesma sequência e dê um nome claro a cada item. Na saída, reutilize essa sequência para procurar o que mudou."
      ],
      "links": [
        {
          "rotulo": "Lei do Inquilinato — trechos dos artigos 22 e 23, leitura web",
          "href": "https://www.planalto.gov.br/ccivil_03/leis/l8245compilado.htm"
        }
      ]
    },
    {
      "titulo": "2. Confira um ambiente por vez",
      "paragrafos": [
        "Use a lista abaixo como roteiro adaptável, registrando somente o que foi observado. Se algo não puder ser testado, anote a limitação e o motivo; não marque como funcionando por suposição."
      ],
      "paragrafosAposItens": [
        "Não desmonte instalações nem improvise testes elétricos ou de gás. Diante de risco ou suspeita de defeito oculto, registre a observação e encaminhe a avaliação a profissional qualificado. Uma inspeção visual não deve virar uma declaração de segurança da instalação."
      ],
      "itens": [
        "Paredes e teto: localização de manchas, descascamentos, furos e fissuras aparentes.",
        "Pisos e rodapés: peças soltas, lascas, riscos e pontos de desnível visíveis.",
        "Portas e janelas: abertura, fechamento, trincos, puxadores, vidros e chaves disponíveis.",
        "Cozinha e banheiro: estado aparente de louças, bancadas, torneiras e sinais de vazamento.",
        "Instalações e equipamentos incluídos: identificação do item e resultado dos testes simples que puderem ser feitos com segurança.",
        "Medidores, quando acessíveis: identificação, leitura e foto correspondente.",
        "Entrega de acessórios: quantidade e identificação de chaves, controles e outros itens previstos."
      ],
      "links": []
    },
    {
      "titulo": "3. Ligue cada foto a uma descrição",
      "paragrafos": [
        "Como método prático, tire uma imagem do ambiente e outra próxima de cada ponto relevante. Identifique os arquivos com ambiente e item: “cozinha-porta-armario” é mais localizável que uma sequência de imagens sem referência. Preserve os arquivos originais e indique no documento a foto correspondente.",
        "Na saída, procure repetir posição e enquadramento. Se a iluminação ou um móvel impedir a comparação, anote isso. A ausência de detalhe numa foto antiga não permite concluir automaticamente que o item estava perfeito.",
        "Exemplo fictício de anotação: “Quarto, porta de entrada: lasca na borda inferior, próxima à dobradiça; foto quarto-porta-detalhe. Fechamento testado, sem dificuldade observada.” A descrição localiza o achado sem atribuir sua causa a alguém."
      ],
      "links": []
    },
    {
      "titulo": "4. Compare antes de atribuir um reparo",
      "paragrafos": [
        "A Lei do Inquilinato prevê a devolução no estado recebido, com exceção das deteriorações do uso normal. Ela também distingue defeitos anteriores à locação e danos provocados pelo locatário ou pessoas sob sua responsabilidade. Essas condições precisam ser preservadas ao analisar uma diferença.",
        "Nossa proposta é organizar cada apontamento em quatro campos: estado na entrada, estado na saída, fotos relacionadas e esclarecimento pendente. Confira também comunicações de manutenção feitas durante o contrato.",
        "Uma mancha pode exigir investigação da origem; uma pintura diferente nas fotos pode refletir iluminação. Não transforme toda mudança em cobrança automática nem prometa uma classificação universal de desgaste. Se houver desacordo sobre causa ou responsabilidade, busque avaliação técnica ou orientação jurídica conforme o problema."
      ],
      "links": [
        {
          "rotulo": "Lei do Inquilinato — trechos dos artigos 22 e 23, leitura web",
          "href": "https://www.planalto.gov.br/ccivil_03/leis/l8245compilado.htm"
        }
      ]
    },
    {
      "titulo": "5. Registre as divergências e preserve as versões",
      "paragrafos": [
        "Antes de encerrar, compartilhe o documento e identifique os pontos que ainda precisam de resposta. Como sugestão, use: “No item [ambiente e objeto], a descrição indica [observação], mas a foto [referência] mostra [diferença]. Podemos conferir e registrar o esclarecimento?”",
        "Não adote um prazo genérico encontrado na internet como se valesse para todo contrato. Confira o procedimento pactuado e registre a comunicação prontamente. Mantenha a versão inicial, as observações e a versão corrigida, com identificação de quem participou da conferência.",
        "Ao final, você deve conseguir localizar o item, entender o que foi observado e saber o que falta esclarecer. Para os documentos da rotina financeira, consulte também o guia sobre comprovante de Pix e recibo de aluguel.",
        "Conheça o Alugando.com para organizar recebimentos e documentos da locação. Este roteiro não pressupõe que o serviço realize vistorias ou emita laudos técnicos.",
        "Base legal consultada em 13 de setembro de 2026: Lei nº 8.245/1991, artigos 22, IV e V, e 23, III, V e IX. Checklist e exemplos são sugestões práticas; não há cálculo tributário neste artigo."
      ],
      "links": [
        {
          "rotulo": "comprovante de Pix e recibo de aluguel",
          "href": "/blog/comprovante-pix-substitui-recibo-aluguel/"
        },
        {
          "rotulo": "Alugando.com",
          "href": "/"
        },
        {
          "rotulo": "Lei do Inquilinato — trechos dos artigos 22 e 23, leitura web",
          "href": "https://www.planalto.gov.br/ccivil_03/leis/l8245compilado.htm"
        }
      ]
    }
  ],
  "chamada": {
    "titulo": "Organize a rotina do aluguel",
    "texto": "Conheça o Alugando.com para organizar recebimentos e documentos da locação.",
    "rotulo": "Conhecer o Alugando.com",
    "href": "/#como-funciona"
  }
},
{
  "id": "seo-daily-2026-09-14_reajuste-aluguel",
  "slug": "reajuste-aluguel-indice-data-valor",
  "titulo": "Reajuste de aluguel: como conferir índice, data e valor",
  "descricao": "Confira índice, período e cálculo do reajuste de aluguel, com exemplos e uma memória de cálculo para explicar o novo valor.",
  "resumo": "Antes de aplicar um percentual ao aluguel, confira três coisas: qual índice foi combinado, qual período entra na conta e a partir de quando o novo valor vale. Um cálculo correto com o índice ou os meses errados continua produzindo uma cobrança que precisa ser revista.\n\nEste roteiro ajuda o proprietário a conferir o reajuste por índice de uma locação urbana comum. Os números são fictícios: não representam o IPCA ou o IGP-M vigente em setembro de 2026.",
  "categoria": "Gestão do aluguel",
  "etapaFunil": "consideracao",
  "intencao": "Conferir cláusula, período acumulado, base de cálculo e comunicação do reajuste de aluguel.",
  "palavrasChave": [
    "demonstrativo de repasse",
    "repasse da imobiliária"
  ],
  "publicadoEm": "2026-09-15",
  "revisadoEm": "2026-09-15",
  "tempoLeitura": "4 min",
  "autor": {
    "nome": "Equipe editorial Alugando.com",
    "cargo": "Redação editorial"
  },
  "revisor": {
    "nome": "Alugando.com",
    "cargo": "Revisão automatizada de fontes"
  },
  "imagem": {
    "src": "/midia/seo/img_1789384014676_4244b1d6.webp",
    "largura": 1536,
    "altura": 1024,
    "alt": "Calendário azul e folha contratual representam a conferência da data e da cláusula de reajuste."
  },
  "imagemInterna": {
    "src": "/midia/seo/img_1789384033071_cfed4110.webp",
    "largura": 1536,
    "altura": 1024,
    "alt": "Calculadora azul e fichas claras representam a conferência do valor-base e do fator de reajuste.",
    "aposSecao": 2,
    "legenda": "Calculadora azul e fichas claras representam a conferência do valor-base e do fator de reajuste."
  },
  "secoes": [
    {
      "titulo": "1. Leia a cláusula e os aditivos antes de buscar o índice",
      "paragrafos": [
        "Separe o contrato e os acordos posteriores. Nossa sugestão é anotar numa ficha: aluguel vigente, índice previsto, periodicidade, data de referência, intervalo de acumulação e primeira competência com o valor novo. Se houver desconto temporário, identifique também o valor-base e as condições do desconto antes de multiplicar o depósito recebido.",
        "Não escolha entre IPCA e IGP-M apenas porque um deles resultou em aumento maior. A Lei do Inquilinato permite às partes combinar um novo aluguel e inserir ou modificar a cláusula de reajuste de comum acordo. Uma alteração negociada deve ser distinguida da aplicação da cláusula que já existe.",
        "Se o texto estiver ausente, contraditório ou tiver sido alterado por acordo, esclareça a regra antes de emitir a cobrança. Não preencha essa lacuna com a configuração padrão de uma calculadora."
      ],
      "links": [
        {
          "rotulo": "Lei 8.245/1991, artigo 18",
          "href": "https://www.planalto.gov.br/ccivil_03/leis/l8245.htm"
        }
      ]
    },
    {
      "titulo": "2. Confira a data e o intervalo de acumulação",
      "paragrafos": [
        "A Lei nº 10.192 admite reajuste por índices nos contratos com duração igual ou superior a um ano e considera nula a estipulação de periodicidade inferior a um ano. Em caso de revisão contratual, a lei também estabelece a data da revisão anterior como início do novo período de correção ou reajuste. Por isso, confira aditivos e revisões em vez de repetir automaticamente o aniversário original.",
        "Como exemplo de leitura, imagine uma cláusula anual com reajuste em setembro de 2026 que determine o acumulado de setembro de 2025 a agosto de 2026. Esse intervalo tem doze meses. A data de pagamento do boleto não muda, por si só, os meses indicados na cláusula.",
        "O exemplo não fixa uma janela universal: use o intervalo aplicável ao seu contrato. Se o índice necessário ainda não foi divulgado, registre a pendência e confira o procedimento pactuado; não use uma previsão como se fosse dado publicado."
      ],
      "links": [
        {
          "rotulo": "Lei 10.192/2001, artigo 2",
          "href": "https://www.planalto.gov.br/ccivil_03/leis/leis_2001/l10192.htm"
        }
      ]
    },
    {
      "titulo": "3. Use o acumulado correspondente, não apenas a taxa do mês",
      "paragrafos": [
        "Uma variação mensal e uma variação acumulada respondem a perguntas diferentes. Para conferir uma atualização pelo período, você pode consultar a Calculadora do Cidadão do Banco Central, selecionando o índice e as datas corretas. A ferramenta inclui as taxas dos meses inicial e final. Confira os dois limites para não inserir um mês a mais.",
        "O Banco Central descreve a atualização como a multiplicação do valor pelo fator acumulado. Quando se parte das taxas mensais, o fator resulta da multiplicação de cada fator mensal; não se obtém simplesmente somando as porcentagens.",
        "Guarde a consulta com nome do índice, período, data de acesso e fator ou percentual utilizado. A calculadora ajuda a conferir a matemática; a escolha da regra contratual continua precisando estar resolvida."
      ],
      "links": [
        {
          "rotulo": "Banco Central — metodologia da correção de valores",
          "href": "https://www3.bcb.gov.br/CALCIDADAO/publico/exibirFormCorrecaoValores.do?aba=&method=exibirFormCorrecaoValores"
        }
      ]
    },
    {
      "titulo": "4. Refaça a conta e separe o aluguel dos outros lançamentos",
      "paragrafos": [
        "Considere um aluguel-base de R$ 2.500,00 e um acumulado fictício de 4,80%, sem desconto, negociação ou outros componentes:"
      ],
      "paragrafosAposItens": [
        "Se a cobrança trouxer R$ 2.670,00 exclusivamente como aluguel nesse mesmo exemplo, há R$ 50,00 a esclarecer. Confira se houve outra base, período diferente ou acordo documentado; a diferença isolada não prova irregularidade.",
        "Na sua conferência, mantenha condomínio, IPTU e outros lançamentos em linhas próprias. Não multiplique o total de um boleto misto pelo índice do aluguel sem verificar a regra de cada componente. Também não trate o depósito líquido da imobiliária como aluguel-base: ele pode refletir descontos que precisam ser identificados.",
        "Se o acumulado for negativo, preserve o sinal na simulação. Por exemplo, R$ 2.500,00 × 0,98 = R$ 2.450,00 com uma variação fictícia de −2%. Esse resultado matemático não decide sozinho a cobrança: confira a cláusula e eventuais acordos, e busque orientação sobre divergências. Não troque o índice nem aplique um piso zero por suposição."
      ],
      "itens": [
        "Fator de correção: 1 + 4,80 ÷ 100 = 1,048.",
        "Novo aluguel: R$ 2.500,00 × 1,048 = R$ 2.620,00.",
        "Diferença mensal: R$ 2.620,00 − R$ 2.500,00 = R$ 120,00."
      ],
      "links": []
    },
    {
      "titulo": "5. Comunique o valor com uma memória de cálculo",
      "paragrafos": [
        "Nossa sugestão de mensagem é: “Conferi o reajuste do contrato [identificação]. A base é R$ [valor], o índice é [nome] e o período é [início a fim]. O acumulado de [percentual] resulta em aluguel de R$ [novo valor], aplicável à competência [mês], conforme a cláusula [referência]. Segue a memória de cálculo para conferência.”",
        "Adapte o texto ao que estiver efetivamente documentado. Se houver dúvida, peça esclarecimento antes de apresentar o resultado como definitivo. Preserve a conta, a comunicação e a resposta; registre separadamente qualquer valor negociado que substitua a aplicação do índice.",
        "Ao concluir, você deve conseguir explicar de onde veio cada número e qual cobrança será afetada. Para a documentação do pagamento, veja comprovante de Pix e recibo de aluguel. Conheça o Alugando.com para organizar recebimentos e documentos da locação.",
        "Fontes consultadas em 14 de setembro de 2026. Este artigo trata de conferência contratual e aritmética, sem apuração de imposto."
      ],
      "links": [
        {
          "rotulo": "comprovante de Pix e recibo de aluguel",
          "href": "/blog/comprovante-pix-substitui-recibo-aluguel/"
        },
        {
          "rotulo": "Alugando.com",
          "href": "/"
        }
      ]
    }
  ],
  "chamada": {
    "titulo": "Organize a rotina do aluguel",
    "texto": "Conheça o Alugando.com para organizar recebimentos e documentos da locação.",
    "rotulo": "Conhecer o Alugando.com",
    "href": "/#como-funciona"
  }
},
{
  "id": "seo-daily-2026-09-15_cobranca-aluguel-atrasado",
  "slug": "aluguel-atrasado-como-cobrar-registrar-negociacao",
  "titulo": "Aluguel atrasado: como cobrar e registrar a negociação",
  "descricao": "Veja como conferir a pendência, enviar uma cobrança objetiva e acompanhar a negociação do aluguel com valores, datas e próximos passos registrados.",
  "resumo": "O aluguel venceu e o pagamento ainda não foi identificado. Antes de mandar uma cobrança, confira o recebimento; depois, transforme a conversa em um próximo passo concreto: valor a esclarecer, data proposta e responsável pelo retorno.\n\nEste roteiro ajuda o proprietário a conduzir o contato e acompanhar uma negociação de aluguel urbano. As mensagens e listas são sugestões de organização, não notificações jurídicas prontas nem uma promessa de recuperação da dívida.",
  "categoria": "Gestão do aluguel",
  "etapaFunil": "consideracao",
  "intencao": "Conduzir primeiro contato e acompanhar proposta, aceite e cumprimento de acordo de aluguel atrasado.",
  "palavrasChave": [
    "demonstrativo de repasse",
    "repasse da imobiliária"
  ],
  "publicadoEm": "2026-09-15",
  "revisadoEm": "2026-09-15",
  "tempoLeitura": "4 min",
  "autor": {
    "nome": "Equipe editorial Alugando.com",
    "cargo": "Redação editorial"
  },
  "revisor": {
    "nome": "Alugando.com",
    "cargo": "Revisão automatizada de fontes"
  },
  "imagem": {
    "src": "/midia/seo/img_1789470491770_2f8b606d.webp",
    "largura": 1536,
    "altura": 1024,
    "alt": "Balões de conversa azul e lima representam um contato claro sobre o aluguel pendente."
  },
  "imagemInterna": {
    "src": "/midia/seo/img_1789470617234_20b36a2f.webp",
    "largura": 1536,
    "altura": 1024,
    "alt": "Pasta azul e fichas organizadas representam o registro e o acompanhamento dos compromissos.",
    "aposSecao": 2,
    "legenda": "Pasta azul e fichas organizadas representam o registro e o acompanhamento dos compromissos."
  },
  "secoes": [
    {
      "titulo": "1. Confirme a pendência antes do primeiro contato",
      "paragrafos": [
        "Confira o vencimento no contrato e nos aditivos, procure o crédito no extrato e verifique se a imobiliária já recebeu. Se houver pagamento sem identificação ou divergência de valor, registre isso como pendência de conferência antes de afirmar que nada foi pago.",
        "A Lei do Inquilinato determina o pagamento pontual do aluguel e dos encargos legal ou contratualmente exigíveis. Sem prazo estipulado, prevê o sexto dia útil do mês seguinte ao vencido. Por isso, não adote uma data padrão sem ler o contrato.",
        "Nossa sugestão é montar uma ficha curta: imóvel, período, vencimento, aluguel, outros componentes discriminados, pagamentos identificados e saldo a conferir. Não aplique multa ou juros encontrados na internet como regra universal; valide a cláusula e o cálculo pertinente antes de informar um total atualizado."
      ],
      "links": [
        {
          "rotulo": "Lei 8.245/1991 — cobrança e encaminhamento",
          "href": "https://www.planalto.gov.br/ccivil_03/leis/l8245compilado.htm"
        }
      ]
    },
    {
      "titulo": "2. Envie uma mensagem objetiva e abra espaço para conferir",
      "paragrafos": [
        "Comece pelo fato observado e pelo pedido. Evite acusações, exposição em grupos e uma sequência de mensagens sem tempo para resposta. Use o canal habitual da locação e mantenha a conversa restrita às pessoas envolvidas.",
        "Modelo para adaptar: “Olá, [nome]. Na conferência do contrato [identificação], ainda não localizei o pagamento do aluguel referente a [período], com vencimento em [data], no valor de R$ [valor]. Se já pagou, pode enviar a referência da transferência para eu conferir? Se continua pendente, podemos combinar uma previsão de pagamento?”",
        "Se o total incluir encargos, envie a discriminação. A lei exige que o locador apresente, quando solicitado, os comprovantes das parcelas cobradas.",
        "Anote a data do contato, a resposta e a próxima ação. “Disse que vai pagar” é pouco útil para acompanhar: registre a data proposta e o que ainda depende de confirmação. O prazo pedido para retorno nessa conversa é uma escolha de organização; não o apresente como prazo legal para despejo."
      ],
      "links": [
        {
          "rotulo": "Lei 8.245/1991 — cobrança e encaminhamento",
          "href": "https://www.planalto.gov.br/ccivil_03/leis/l8245compilado.htm"
        }
      ]
    },
    {
      "titulo": "3. Separe proposta, acordo aceito e pagamento",
      "paragrafos": [
        "Se houver pedido de parcelamento, confira a proposta inteira antes de responder. Além da dívida antiga, o aluguel corrente continuará precisando de tratamento próprio. Nossa recomendação é esclarecer se os valores propostos permitem acompanhar essas duas obrigações sem misturá-las.",
        "Registre por escrito os pontos negociados:"
      ],
      "paragrafosAposItens": [
        "Não marque a proposta como aceita enquanto faltar uma resposta expressa. Para formalizar mudanças que afetem garantias, descontos condicionais ou consequências de novo atraso, procure orientação jurídica sobre a redação e as anuências necessárias.",
        "Uma mensagem de fechamento pode ser: “Para confirmar o que conversamos, a proposta é [resumo de valores e datas], referente a [períodos]. Os aluguéis correntes ficam [tratamento combinado]. Podemos conferir os pontos pendentes e registrar a versão final?” Esse texto ajuda a organizar a conversa; não substitui o instrumento adequado ao caso."
      ],
      "itens": [
        "Quais períodos e componentes entram no acordo.",
        "Valor total conferido, parcelas, datas e forma de pagamento.",
        "Como ficam os aluguéis correntes, fora do parcelamento.",
        "Descontos ou encargos negociados e as condições efetivamente aceitas.",
        "Pontos ainda pendentes e quem precisa confirmar o ajuste."
      ],
      "links": []
    },
    {
      "titulo": "4. Acompanhe o combinado com uma próxima ação",
      "paragrafos": [
        "Use uma linha por compromisso, com data prevista, situação e próximo contato. Você pode adotar quatro estados simples: aguardando resposta, proposta em conferência, acordo confirmado e pagamento conferido. Preserve a versão aceita quando houver alteração posterior.",
        "Se a data combinada passar, retome o compromisso específico: “Na nossa conversa de [data], ficou previsto [pagamento]. Ainda não identifiquei o crédito. Houve algum problema? Preciso confirmar a situação para atualizar o acompanhamento.”",
        "Uma promessa nova não apaga o histórico da anterior. Registre o que mudou e por quê, sem tratar a renegociação como dinheiro recebido. Ao entrar o pagamento, confira o crédito e vincule-o ao compromisso correspondente. Para documentar a quitação, use o guia sobre comprovante de Pix e recibo de aluguel."
      ],
      "links": [
        {
          "rotulo": "comprovante de Pix e recibo de aluguel",
          "href": "/blog/comprovante-pix-substitui-recibo-aluguel/"
        }
      ]
    },
    {
      "titulo": "5. Reconheça quando a conversa precisa de outro encaminhamento",
      "paragrafos": [
        "Sem resposta, com divergência relevante ou descumprimentos repetidos, reúna contrato, demonstrativo da dívida, pagamentos e comunicações para orientação jurídica. Não anuncie medida ou prazo processual que ainda não foi avaliado.",
        "A falta de pagamento pode fundamentar o desfazimento da locação; para reaver o imóvel, a lei prevê ação de despejo, ressalvada a hipótese legal de desapropriação. Isso não autoriza transformar a cobrança cotidiana em retomada por conta própria.",
        "O objetivo desta rotina é terminar cada contato sabendo o que falta resolver e qual é o próximo passo. O tratamento fiscal do recebimento fica no guia sobre aluguel recebido com atraso e mês de apuração, sem misturar a negociação com o cálculo do imposto.",
        "Conheça o Alugando.com para organizar cobranças, recebimentos e documentos da locação.",
        "Base legal conferida em 15 de setembro de 2026: Lei nº 8.245/1991, artigos 5º, 9º, III, 22, IX, e 23, I. Este roteiro não calcula imposto nem estabelece percentuais de multa ou juros."
      ],
      "links": [
        {
          "rotulo": "aluguel recebido com atraso e mês de apuração",
          "href": "/blog/aluguel-recebido-atraso-mes-imposto/"
        },
        {
          "rotulo": "Alugando.com",
          "href": "/"
        },
        {
          "rotulo": "Lei 8.245/1991 — cobrança e encaminhamento",
          "href": "https://www.planalto.gov.br/ccivil_03/leis/l8245compilado.htm"
        }
      ]
    }
  ],
  "chamada": {
    "titulo": "Organize a rotina do aluguel",
    "texto": "Conheça o Alugando.com para organizar cobranças, recebimentos e documentos da locação.",
    "rotulo": "Conhecer o Alugando.com",
    "href": "/#como-funciona"
  }
},
{
  "id": "seo-daily-2026-09-08_deducoes-aluguel",
  "slug": "iptu-condominio-taxa-imobiliaria-aluguel-tributavel",
  "titulo": "IPTU, condomínio e taxa de imobiliária: o que pode reduzir o aluguel tributável?",
  "descricao": "Entenda quando IPTU, condomínio e taxa de imobiliária podem reduzir o aluguel tributável e confira um exemplo sem descontar a mesma despesa duas vezes.",
  "resumo": "Antes de calcular o imposto sobre aluguel, separe o que foi recebido dos encargos que podem ser excluídos. IPTU, condomínio e administração exigem conferir quem realmente suportou a despesa: ter um boleto no seu nome não resolve essa análise sozinho. Este guia é para o proprietário pessoa física residente no Brasil e trata da organização dos recebimentos do ano-calendário de 2026. A declaração entregue em 2026 trata, em regra, dos rendimentos de 2025; os recebimentos de 2026 pertencem ao exercício de 2027. Não use exemplos de tabelas de anos anteriores para calcular o imposto atual.",
  "categoria": "Imposto sobre aluguel",
  "etapaFunil": "consideracao",
  "intencao": "Conferir exclusões permitidas do aluguel tributável e evitar descontar despesas do inquilino ou duplicar taxa de administração.",
  "palavrasChave": [
    "imposto sobre aluguel",
    "iptu condominio taxa imobiliaria aluguel tributavel"
  ],
  "publicadoEm": "2026-09-10",
  "revisadoEm": "2026-09-10",
  "tempoLeitura": "5 min",
  "autor": {
    "nome": "Equipe editorial Alugando.com",
    "cargo": "Redação editorial"
  },
  "revisor": {
    "nome": "Alugando.com",
    "cargo": "Revisão automatizada de fontes"
  },
  "imagem": {
    "src": "/midia/seo/img_1788874347708_d9200f85.webp",
    "largura": 1536,
    "altura": 1024,
    "alt": "Casa e documentos representam a conferência dos encargos do aluguel."
  },
  "imagemInterna": {
    "src": "/midia/seo/img_1788874394488_d16b2f70.webp",
    "largura": 1536,
    "altura": 1024,
    "alt": "Exemplo hipotético: aluguel de 4 mil reais menos 1.200 reais de encargos admitidos resulta em 2.800 reais após exclusões, não no imposto devido.",
    "aposSecao": 3,
    "legenda": "Exemplo hipotético: aluguel de 4 mil reais menos 1.200 reais de encargos admitidos resulta em 2.800 reais após exclusões, não no imposto devido."
  },
  "secoes": [
    {
      "titulo": "A condição central: o encargo precisa ser do locador",
      "paragrafos": [
        "O artigo 42 do Regulamento do Imposto de Renda permite retirar do aluguel determinadas despesas quando o ônus foi exclusivamente do locador. A lista inclui tributos e emolumentos incidentes sobre o imóvel, aluguel pago na sublocação, despesas de cobrança ou recebimento e condomínio.",
        "Na prática, confronte contrato, comprovante de pagamento e demonstrativo. Se o inquilino custeou o IPTU ou o condomínio, não trate esse gasto como se fosse suportado pelo proprietário. Se houve reembolso, reconcilie quem ficou com o encargo antes de fazer a exclusão."
      ],
      "links": [
        {
          "rotulo": "Receita Federal: rendimentos",
          "href": "https://www.gov.br/receitafederal/pt-br/assuntos/meu-imposto-de-renda/pagamento/carne-leao/rendimentos"
        },
        {
          "rotulo": "Receita Federal: ano",
          "href": "https://www.gov.br/receitafederal/pt-br/assuntos/meu-imposto-de-renda/tabelas/2026"
        },
        {
          "rotulo": "rir-art42",
          "href": "https://www2.camara.leg.br/legin/fed/decret/2018/decreto-9580-22-novembro-2018-787360-normaatualizada-pe.html"
        },
        {
          "rotulo": "Receita Federal: despesas",
          "href": "https://www.gov.br/receitafederal/pt-br/acesso-a-informacao/perguntas-frequentes/imposto-de-renda/dirpf/deducoes/outras-despesas-que-sao-abatidas"
        }
      ]
    },
    {
      "titulo": "IPTU e condomínio: confira o pagamento e o período",
      "paragrafos": [
        "O IPTU do imóvel locado pode ser excluído quando pago pelo locador nas condições admitidas. A Receita esclarece que o pagamento pode ser à vista ou parcelado, mesmo quando o aluguel foi recebido em apenas parte do ano, respeitado o ano-calendário correspondente.",
        "Isso não autoriza transformar todo boleto previsto para o ano em despesa já paga. Guarde a guia quitada e identifique o imóvel. Para condomínio, preserve o demonstrativo e o comprovante que evidenciem o encargo do locador. Valores previstos, pendentes e pagos precisam ficar separados no controle.",
        "Há cuidados adicionais quando o imóvel ficou vazio ou quando o IPTU se refere a anos anteriores. A Solução de Consulta Cosit nº 213/2019 diferencia o IPTU do ano corrente de débitos anteriores e exige, para o caso de IPTU atrasado analisado, pagamento durante a vigência da locação e no ano de recebimento das receitas. Ela também veda transportar saldo negativo para anos seguintes. Não aplique esse caso automaticamente a qualquer despesa."
      ],
      "links": [
        {
          "rotulo": "Receita Federal: cosit",
          "href": "https://normas.receita.fazenda.gov.br/sijut2consulta/anexoOutros.action?idArquivoBinario=52938"
        },
        {
          "rotulo": "Receita Federal: rendimentos",
          "href": "https://www.gov.br/receitafederal/pt-br/assuntos/meu-imposto-de-renda/pagamento/carne-leao/rendimentos"
        }
      ]
    },
    {
      "titulo": "Taxa de imobiliária: desconte uma única vez",
      "paragrafos": [
        "A taxa de administração paga pelo proprietário pode reduzir o aluguel informado. A orientação da Receita para aluguéis intermediados por imobiliária manda conferir o aluguel menos essa taxa. O repasse líquido, porém, precisa ser conciliado com o demonstrativo para saber quais descontos já ocorreram.",
        "Exemplo: a imobiliária recebe R$ 4.000 de aluguel e retém R$ 400 de administração suportada pelo locador. O repasse é de R$ 3.600. Subtrair R$ 400 novamente desse repasse reduziria o rendimento duas vezes pelo mesmo gasto. Comece pelo aluguel bruto e identifique cada exclusão uma vez.",
        "Informar o pagamento ao administrador na declaração também não cria uma segunda dedução. O manual da Receita orienta registrar esses pagamentos na categoria própria de administrador de imóveis. Separe a informação da despesa da exclusão já considerada no rendimento."
      ],
      "links": [
        {
          "rotulo": "Receita Federal: imobiliaria",
          "href": "https://www.gov.br/receitafederal/pt-br/assuntos/meu-imposto-de-renda/malha-fiscal/antecipacao/rendimentos-recebidos-de-empresa/recebo-alugueis-de-uma-imobiliaria"
        },
        {
          "rotulo": "Receita Federal: manual",
          "href": "https://www.gov.br/receitafederal/pt-br/assuntos/meu-imposto-de-renda/preenchimento/manual-mir/pagamentos-ou-doacoes/despesas-nao-dedutiveis"
        }
      ]
    },
    {
      "titulo": "Um exemplo para conferir os documentos",
      "paragrafos": [
        "Imagine um aluguel bruto de R$ 4.000 recebido em setembro de 2026. No exemplo, o locador suporta exclusivamente e paga no mesmo mês R$ 400 de administração, R$ 250 de IPTU e R$ 550 de condomínio, todos referentes ao imóvel alugado e sem reembolso pelo inquilino.",
        "Os R$ 2.800 são o resultado dessa conferência, não o imposto devido. O cálculo do imposto depende da apuração completa e das regras aplicáveis. No Carnê-Leão Web, a orientação é informar o aluguel bruto e lançar o total admissível no campo de exclusões/deduções."
      ],
      "itens": [
        "Aluguel bruto recebido: R$ 4.000.",
        "Encargos admitidos no exemplo: R$ 400 + R$ 250 + R$ 550 = R$ 1.200.",
        "Rendimento após essas exclusões: R$ 4.000 − R$ 1.200 = R$ 2.800."
      ],
      "links": [
        {
          "rotulo": "Receita Federal: rendimentos",
          "href": "https://www.gov.br/receitafederal/pt-br/assuntos/meu-imposto-de-renda/pagamento/carne-leao/rendimentos"
        }
      ]
    },
    {
      "titulo": "Nem todo gasto com o imóvel reduz o rendimento",
      "paragrafos": [
        "Não confunda as exclusões do aluguel com uma autorização geral para deduzir qualquer custo. A Receita informa que prestação da casa própria e juros de financiamento não têm previsão de dedução. O aluguel da casa onde o proprietário mora também não se confunde com o aluguel pago em uma sublocação."
      ],
      "links": [
        {
          "rotulo": "Receita Federal: despesas",
          "href": "https://www.gov.br/receitafederal/pt-br/acesso-a-informacao/perguntas-frequentes/imposto-de-renda/dirpf/deducoes/outras-despesas-que-sao-abatidas"
        },
        {
          "rotulo": "rir-art42",
          "href": "https://www2.camara.leg.br/legin/fed/decret/2018/decreto-9580-22-novembro-2018-787360-normaatualizada-pe.html"
        }
      ]
    },
    {
      "titulo": "Feche o mês com uma conferência curta",
      "paragrafos": [
        "Como rotina de organização, reúna o contrato, o demonstrativo da imobiliária, o comprovante do aluguel e os encargos pagos. Marque o que foi custeado pelo proprietário, o que foi pago pelo inquilino e o que ainda precisa de esclarecimento. Isso deixa a apuração verificável sem depender da memória.",
        "Confira também quem é o locatário: a imobiliária intermediária não transforma o aluguel de pessoa física em rendimento de pessoa jurídica. Quando o inquilino é empresa, o enquadramento é diferente. Preserve essa identificação junto dos documentos.",
        "Para estruturar esse histórico, veja como organizar recebimentos de aluguel mês a mês. Conheça o Alugando.com para organizar a rotina dos seus aluguéis.",
        "Fontes oficiais conferidas em 8 de setembro de 2026. Os valores do exemplo são hipotéticos e demonstram apenas as exclusões, sem prometer redução específica de imposto."
      ],
      "links": [
        {
          "rotulo": "como organizar recebimentos de aluguel mês a mês",
          "href": "https://alugando.com/blog/como-organizar-recebimentos-de-aluguel/"
        },
        {
          "rotulo": "Alugando.com",
          "href": "https://alugando.com/"
        },
        {
          "rotulo": "Receita Federal: imobiliaria",
          "href": "https://www.gov.br/receitafederal/pt-br/assuntos/meu-imposto-de-renda/malha-fiscal/antecipacao/rendimentos-recebidos-de-empresa/recebo-alugueis-de-uma-imobiliaria"
        }
      ]
    }
  ],
  "chamada": {
    "titulo": "Organize a rotina do aluguel",
    "texto": "Organize recebimentos e comprovantes de cada imóvel no Alugando.com.",
    "rotulo": "Conhecer o Alugando.com",
    "href": "/#como-funciona"
  }
},
{
  "id": "seo-daily-2026-09-09-irrf_irrf-aluguel-empresa",
  "slug": "aluguel-empresa-documentos-irrf",
  "titulo": "Imóvel alugado para empresa: quais documentos conferir sobre o IR retido",
  "descricao": "Saiba como conferir contrato, demonstrativo, depósito e comprovante de rendimentos do aluguel pago por empresa, sem confundir o líquido com o IRRF.",
  "resumo": "Um roteiro para comparar contrato, demonstrativo, depósito e comprovante de rendimentos sem confundir aluguel líquido com imposto pago.",
  "categoria": "Imposto sobre aluguel",
  "etapaFunil": "consideracao",
  "intencao": "Conferir documentos, fonte pagadora, rendimento e IRRF do aluguel pago por empresa ao proprietário pessoa física residente no Brasil.",
  "palavrasChave": [
    "imposto sobre aluguel",
    "aluguel empresa documentos irrf"
  ],
  "publicadoEm": "2026-09-10",
  "revisadoEm": "2026-09-10",
  "tempoLeitura": "5 min",
  "autor": {
    "nome": "Equipe editorial Alugando.com",
    "cargo": "Redação editorial"
  },
  "revisor": {
    "nome": "Alugando.com",
    "cargo": "Revisão automatizada de fontes"
  },
  "imagem": {
    "src": "/midia/seo/img_1788952363064_bf081fd4.webp",
    "largura": 1536,
    "altura": 1024,
    "alt": "Prédio empresarial, casa e pasta de documentos representam o aluguel para pessoa jurídica."
  },
  "imagemInterna": {
    "src": "/midia/seo/img_1788952469560_e0644732.webp",
    "largura": 1536,
    "altura": 1024,
    "alt": "Contrato, demonstrativo e comprovante de depósito conectados para ilustrar a conferência do recebimento.",
    "aposSecao": 3,
    "legenda": "Contrato, demonstrativo e comprovante de depósito conectados para ilustrar a conferência do recebimento."
  },
  "secoes": [
    {
      "titulo": "Comece pelo contrato, não pelo nome no extrato",
      "paragrafos": [
        "Este guia trata do proprietário pessoa física residente no Brasil que recebe aluguel de uma pessoa jurídica, no ano-calendário de 2026. Nesse caso, o aluguel está sujeito ao imposto de renda retido na fonte pela empresa pagadora, conforme a legislação aplicável. O mesmo rendimento não deve ser recolhido novamente como aluguel recebido de pessoa física no Carnê-Leão.",
        "Confira quem é o locatário no contrato. Um depósito com o nome da imobiliária pode ser apenas um repasse: se o inquilino é pessoa física, a intermediação não transforma o aluguel em rendimento recebido de empresa. A Receita diferencia expressamente essas situações."
      ],
      "links": [
        {
          "rotulo": "Receita Federal: irrf",
          "href": "https://www.gov.br/receitafederal/pt-br/acesso-a-informacao/perguntas-frequentes/sped/efd-reinf/efdr/2-eventos-da-efd-reinf/2-13-9-e-necessario-informar"
        },
        {
          "rotulo": "Lei 7.713/1988, artigo 7º, II",
          "href": "https://www.planalto.gov.br/ccivil_03/leis/l7713.htm"
        },
        {
          "rotulo": "Receita Federal: imobiliaria",
          "href": "https://www.gov.br/receitafederal/pt-br/assuntos/meu-imposto-de-renda/malha-fiscal/antecipacao/rendimentos-recebidos-de-empresa/recebo-alugueis-de-uma-imobiliaria"
        }
      ]
    },
    {
      "titulo": "Separe quatro documentos com funções diferentes",
      "paragrafos": [
        "Para organizar a conferência, mantenha juntos o contrato, o demonstrativo mensal, o comprovante bancário e o comprovante de rendimentos fornecido pela fonte pagadora. Esta é uma sugestão de controle: nenhum documento deve ser lido isoladamente como explicação de todos os valores.",
        "A orientação da Receita para aluguéis pagos por pessoa jurídica prevê comprovante anual com o rendimento atribuído ao beneficiário e o respectivo imposto retido. Em situações de copropriedade, a identificação de cada beneficiário merece conferência específica."
      ],
      "itens": [
        "Contrato: confira locatário, proprietário beneficiário, imóvel e condições do aluguel.",
        "Demonstrativo mensal: identifique aluguel, encargos, taxa de administração e eventual IRRF em campos separados.",
        "Comprovante bancário: compare a data e o valor efetivamente creditado com o líquido esperado.",
        "Comprovante de rendimentos: confronte a fonte pagadora, o beneficiário, o período, os rendimentos e o imposto retido informado."
      ],
      "links": [
        {
          "rotulo": "Solução de Consulta Cosit 55/2020",
          "href": "https://normas.receita.fazenda.gov.br/sijut2consulta/anexoOutros.action?idArquivoBinario=56939"
        }
      ]
    },
    {
      "titulo": "O líquido recebido não é o rendimento inteiro",
      "paragrafos": [
        "Uma diferença entre o aluguel do contrato e o depósito pode ter mais de uma explicação. Pode haver taxa de administração, encargos, retenção ou pagamento parcial. Antes de classificar a diferença como imposto, procure sua discriminação no demonstrativo.",
        "O IRRF é imposto retido, não uma despesa de administração do imóvel. A Receita enumera os encargos que podem ser excluídos do aluguel quando suportados exclusivamente pelo locador; não se deve misturar essa conferência com o campo do imposto já retido.",
        "Exemplo de conferência, sem cálculo tributário: o contrato prevê R$ 6.000 e o banco mostra R$ 5.200. A diferença é R$ 800. Esse cálculo apenas identifica o valor a explicar; não prova que R$ 800 sejam IRRF. Solicite a decomposição dos valores e confira cada parcela antes de registrar o fechamento."
      ],
      "links": [
        {
          "rotulo": "Receita Federal: capital",
          "href": "https://www.gov.br/receitafederal/pt-br/assuntos/meu-imposto-de-renda/preenchimento/manual-mir/rendimentos/rendimentos-do-capital"
        }
      ]
    },
    {
      "titulo": "Confira o cálculo do mês de 2026",
      "paragrafos": [
        "Para verificar uma retenção, peça a memória de cálculo à empresa ou ao responsável pela apuração. Ela deve permitir entender quais rendimentos, exclusões, deduções e regras foram considerados. Não tente deduzir a alíquota apenas dividindo a diferença do depósito pelo aluguel contratual.",
        "A Receita publica separadamente a tabela progressiva e a redução mensal aplicável desde janeiro de 2026. Usar apenas uma tabela de ano anterior, ou aplicar uma porcentagem fixa sobre qualquer aluguel, não reproduz necessariamente a apuração correta. O resultado pode ser zero; estar sujeito à retenção não significa haver valor positivo de imposto em todo pagamento.",
        "Se o demonstrativo indicar imposto zero, confira a justificativa. Se não trouxer informação alguma, registre a pendência: ausência de informação não é confirmação de ausência de imposto."
      ],
      "links": [
        {
          "rotulo": "Receita Federal: tabela-2026",
          "href": "https://www.gov.br/receitafederal/pt-br/assuntos/meu-imposto-de-renda/tabelas/2026"
        },
        {
          "rotulo": "lei-reducao",
          "href": "https://www.planalto.gov.br/ccivil_03/_ato2023-2026/2025/lei/l15270.htm"
        }
      ]
    },
    {
      "titulo": "Quando há imobiliária, confira também o repasse",
      "paragrafos": [
        "Peça um demonstrativo que permita ligar o pagamento do locatário ao crédito destinado ao proprietário. Guarde as datas separadamente e identifique quem prestou cada informação. Isso ajuda a localizar diferenças sem confundir a administradora com a fonte pagadora do aluguel.",
        "A Receita orienta, na Dimob, que rendimentos e imposto retido correspondentes a vários contratos administrados para o mesmo locatário sejam somados no respectivo mês do pagamento. Por isso, um total consolidado pode exigir abrir os demonstrativos de mais de um imóvel para conferir a composição. Essa orientação sobre a declaração da imobiliária não dispensa a análise de cada recebimento no seu controle."
      ],
      "links": [
        {
          "rotulo": "Receita Federal: imobiliaria",
          "href": "https://www.gov.br/receitafederal/pt-br/assuntos/meu-imposto-de-renda/malha-fiscal/antecipacao/rendimentos-recebidos-de-empresa/recebo-alugueis-de-uma-imobiliaria"
        },
        {
          "rotulo": "Receita Federal: Dimob, pergunta 71",
          "href": "https://www.gov.br/receitafederal/pt-br/assuntos/orientacao-tributaria/declaracoes-e-demonstrativos/dimob/perguntas-e-respostas"
        }
      ]
    },
    {
      "titulo": "Não resolva uma divergência pagando outro imposto por conta própria",
      "paragrafos": [
        "Se contrato, demonstrativo e comprovante de rendimentos não concordarem, reúna os documentos e peça esclarecimento ou correção à fonte que emitiu a informação. Registre o mês, o valor divergente e a resposta recebida. Essa trilha facilita a conferência posterior.",
        "A falta de retenção em um aluguel pago por empresa não transforma o rendimento em aluguel recebido de pessoa física. O enquadramento depende da fonte e do beneficiário; emitir Carnê-Leão por iniciativa própria não é um modo de conciliar documentos inconsistentes."
      ],
      "links": [
        {
          "rotulo": "Receita Federal: irrf",
          "href": "https://www.gov.br/receitafederal/pt-br/acesso-a-informacao/perguntas-frequentes/sped/efd-reinf/efdr/2-eventos-da-efd-reinf/2-13-9-e-necessario-informar"
        }
      ]
    },
    {
      "titulo": "Feche o ano com os meses já conciliados",
      "paragrafos": [
        "Este roteiro acompanha recebimentos de 2026. O exercício da declaração é diferente do ano-calendário: a Receita identifica a incidência anual relativa a 2026 como exercício de 2027. Um comprovante referente a 2025 não substitui o acompanhamento dos pagamentos de 2026.",
        "No fechamento, compare os totais por fonte pagadora e preserve as diferenças ainda não esclarecidas. O objetivo é chegar à declaração com documentos coerentes, sem reconstruir doze meses a partir do saldo bancário.",
        "Veja também o guia Imposto sobre aluguel: quando usar o Carnê-Leão. Para organizar cobranças, recebimentos, recibos e documentos, conheça o Alugando.com — essa rotina continua útil mesmo quando não há imposto mensal a pagar."
      ],
      "links": [
        {
          "rotulo": "Imposto sobre aluguel: quando usar o Carnê-Leão",
          "href": "https://alugando.com/blog/imposto-aluguel-quando-usar-carne-leao/"
        },
        {
          "rotulo": "Receita Federal: tabela-2026",
          "href": "https://www.gov.br/receitafederal/pt-br/assuntos/meu-imposto-de-renda/tabelas/2026"
        }
      ]
    }
  ],
  "chamada": {
    "titulo": "Organize a rotina do aluguel",
    "texto": "Organize cobranças, recebimentos, recibos e documentos da locação no Alugando.com.",
    "rotulo": "Conhecer o Alugando.com",
    "href": "/#como-funciona"
  }
},
{
  "id": "seo-daily-2026-09-10_aluguel-atrasado-mes-imposto",
  "slug": "aluguel-recebido-atraso-mes-imposto",
  "titulo": "Aluguel recebido com atraso: em qual mês apurar o imposto?",
  "descricao": "Veja em qual mês apurar aluguel recebido com atraso, como tratar parcelas e repasses da imobiliária e distinguir ano-calendário de exercício.",
  "resumo": "O aluguel venceu em julho, mas o inquilino pagou em setembro. Para o proprietário pessoa física residente no Brasil, o ponto de partida é o recebimento, e não o mês escrito no boleto. A Lei 7.713 prevê a incidência mensal conforme os rendimentos são percebidos. No aluguel administrado por imobiliária, vale a data do pagamento do locatário à administradora, mesmo que o repasse ao proprietário aconteça depois. Este roteiro considera recebimentos no ano-calendário de 2026 e locatário pessoa física. Ele ajuda a organizar datas e valores antes da apuração; os exemplos não calculam o imposto devido.",
  "categoria": "Imposto sobre aluguel",
  "etapaFunil": "consideracao",
  "intencao": "Identificar mês e ano de apuração de aluguel atrasado, acumulado ou parcelado, distinguindo pagamento ao administrador e repasse.",
  "palavrasChave": [
    "imposto sobre aluguel",
    "aluguel recebido atraso mes imposto"
  ],
  "publicadoEm": "2026-09-10",
  "revisadoEm": "2026-09-10",
  "tempoLeitura": "5 min",
  "autor": {
    "nome": "Equipe editorial Alugando.com",
    "cargo": "Redação editorial"
  },
  "revisor": {
    "nome": "Alugando.com",
    "cargo": "Revisão automatizada de fontes"
  },
  "imagem": {
    "src": "/midia/seo/img_1789038530812_30e598ed.webp",
    "largura": 1536,
    "altura": 1024,
    "alt": "Casa, calendário e dois comprovantes representam aluguéis atrasados recebidos no mesmo mês."
  },
  "imagemInterna": {
    "src": "/midia/seo/img_1789038569178_01084980.webp",
    "largura": 1536,
    "altura": 1024,
    "alt": "Exemplo de 2026: pagamento à imobiliária em agosto e repasse em setembro; o mês da apuração é agosto.",
    "aposSecao": 3,
    "legenda": "Exemplo de 2026: pagamento à imobiliária em agosto e repasse em setembro; o mês da apuração é agosto."
  },
  "secoes": [
    {
      "titulo": "Guarde três datas, porque cada uma responde a uma pergunta",
      "paragrafos": [
        "No controle da locação, vale manter campos separados para o vencimento contratual, o pagamento do inquilino e o repasse da imobiliária. O vencimento ajuda a identificar a dívida e o atraso. O pagamento indica quando o locatário entregou o valor. O repasse explica quando a administradora transferiu o dinheiro ao proprietário.",
        "Essa separação evita escolher o mês fiscal apenas olhando o extrato da conta do locador. A Receita considera o pagamento ao proprietário ou à administradora, inclusive quando esta não informa o recebimento ao dono do imóvel.",
        "Como sugestão prática, vincule cada registro ao contrato, ao imóvel e ao comprovante correspondente. Preserve a referência do aluguel original mesmo quando o pagamento acontecer em outro mês: ela continua necessária para saber qual dívida foi quitada."
      ],
      "links": [
        {
          "rotulo": "Lei 7.713, artigo 2",
          "href": "https://www.planalto.gov.br/ccivil_03/leis/l7713.htm"
        },
        {
          "rotulo": "Receita Federal: rendimentos",
          "href": "https://www.gov.br/receitafederal/pt-br/assuntos/meu-imposto-de-renda/pagamento/carne-leao/rendimentos"
        }
      ]
    },
    {
      "titulo": "Dois aluguéis atrasados pagos juntos entram no mês do recebimento",
      "paragrafos": [
        "Imagine dois aluguéis de R$ 3.000, vencidos em julho e agosto de 2026. O inquilino pessoa física paga os R$ 6.000 diretamente ao proprietário em 10 de setembro. Neste exemplo, não há juros, multa, encargos, compensação ou retenção.",
        "A soma recebida é R$ 3.000 + R$ 3.000 = R$ 6.000. O recebimento pertence a setembro, embora quite dívidas de dois meses anteriores. Não se divide artificialmente esse pagamento entre julho e agosto para reproduzir os vencimentos do contrato. Essa é a aplicação da regra de incidência mensal por recebimento.",
        "R$ 6.000 é o total bruto do exemplo, não uma base final nem o imposto. A apuração precisa considerar os demais rendimentos sujeitos ao Carnê-Leão no mês, as exclusões e deduções cabíveis e as regras vigentes. A Receita publica a tabela progressiva e a redução mensal de 2026 separadamente: concluir o imposto só pelo valor de um aluguel pode levar a erro."
      ],
      "links": [
        {
          "rotulo": "Lei 7.713, artigo 2",
          "href": "https://www.planalto.gov.br/ccivil_03/leis/l7713.htm"
        },
        {
          "rotulo": "Receita Federal: carne",
          "href": "https://www.gov.br/receitafederal/pt-br/assuntos/meu-imposto-de-renda/pagamento/carne-leao/carne-leao"
        },
        {
          "rotulo": "Receita Federal: ano",
          "href": "https://www.gov.br/receitafederal/pt-br/assuntos/meu-imposto-de-renda/tabelas/2026"
        }
      ]
    },
    {
      "titulo": "O repasse no mês seguinte não muda a data do pagamento",
      "paragrafos": [
        "Considere outra situação: o inquilino paga à imobiliária em 28 de agosto de 2026, mas o proprietário recebe o repasse em 3 de setembro. O pagamento do aluguel ocorreu em agosto para fins da regra indicada pela Receita. O crédito posterior na conta do proprietário não desloca esse recebimento para setembro.",
        "Peça à administradora um demonstrativo que mostre a data do pagamento do locatário, o valor bruto, os descontos discriminados e a data do repasse. Se o extrato bancário e o demonstrativo parecem divergir, investigue qual evento cada documento registra antes de alterar a apuração."
      ],
      "links": [
        {
          "rotulo": "Receita Federal: rendimentos",
          "href": "https://www.gov.br/receitafederal/pt-br/assuntos/meu-imposto-de-renda/pagamento/carne-leao/rendimentos"
        }
      ]
    },
    {
      "titulo": "E se o acordo for pago em parcelas?",
      "paragrafos": [
        "Suponha que uma dívida de aluguel seja quitada com R$ 1.000 em setembro e R$ 2.000 em outubro de 2026, ambos pagos diretamente ao proprietário, sem outros componentes. A aplicação da regra de recebimento leva R$ 1.000 a setembro e R$ 2.000 a outubro. A soma de R$ 3.000 fecha o acordo, mas não transforma as duas entradas em um único recebimento de setembro.",
        "No controle, mantenha o saldo ainda pendente e vincule cada parcela ao acordo. Não registre como recebida a parte apenas prometida. Este exemplo trata de pagamento normal em dinheiro: depósitos judiciais e compensações exigem conferir sua regra específica. A Receita, por exemplo, condiciona a incidência sobre aluguel depositado em juízo à liberação pela autoridade judicial."
      ],
      "links": [
        {
          "rotulo": "Lei 7.713, artigo 2",
          "href": "https://www.planalto.gov.br/ccivil_03/leis/l7713.htm"
        },
        {
          "rotulo": "Receita Federal: rendimentos",
          "href": "https://www.gov.br/receitafederal/pt-br/assuntos/meu-imposto-de-renda/pagamento/carne-leao/rendimentos"
        }
      ]
    },
    {
      "titulo": "Receber atrasado e pagar o imposto atrasado são situações diferentes",
      "paragrafos": [
        "O atraso do inquilino não significa, por si só, que o Carnê-Leão também esteja vencido. Havendo imposto a pagar, o prazo é o último dia útil do mês seguinte ao recebimento. Assim, um recebimento de setembro de 2026 tem seu prazo de recolhimento no último dia útil de outubro.",
        "Se o imposto daquele recebimento já venceu e não foi pago, a regularização deve considerar os acréscimos legais do tributo. A Receita mantém orientação específica para pagamento em atraso. Não confunda esses acréscimos com a multa contratual eventualmente cobrada do inquilino."
      ],
      "links": [
        {
          "rotulo": "Receita Federal: carne",
          "href": "https://www.gov.br/receitafederal/pt-br/assuntos/meu-imposto-de-renda/pagamento/carne-leao/carne-leao"
        },
        {
          "rotulo": "Receita Federal: atraso",
          "href": "https://www.gov.br/receitafederal/pt-br/assuntos/orientacao-tributaria/pagamentos-e-parcelamentos/pagamento-em-atraso/pagamento-em-atraso"
        }
      ]
    },
    {
      "titulo": "Confira os componentes e o ano antes de fechar",
      "paragrafos": [
        "Se o pagamento real incluir juros, multa, taxas ou outros valores, discrimine cada parcela e confira seu tratamento antes de aplicar o exemplo simplificado. Não trate automaticamente todo desconto do demonstrativo como dedução fiscal. As exclusões do aluguel dependem das condições legais, incluindo o encargo suportado exclusivamente pelo locador.",
        "Na virada do ano, a referência da dívida também não substitui a data do recebimento. Um aluguel vencido em dezembro de 2025 e pago diretamente pelo inquilino em janeiro de 2026, nas mesmas condições simples dos exemplos, integra os recebimentos de 2026. Ano-calendário e exercício da declaração são distintos: a Receita identifica a incidência anual relativa a 2026 como exercício de 2027.",
        "Por fim, confirme quem é o locatário. A imobiliária como intermediária não transforma aluguel de pessoa física em rendimento de empresa. Quando o inquilino é pessoa jurídica, o enquadramento é diferente; não aplique automaticamente este roteiro de Carnê-Leão ao mesmo rendimento.",
        "Para entender o enquadramento, leia quando o proprietário precisa usar o Carnê-Leão. No fechamento mensal, concilie dívida, recebimento e documentos. O Alugando.com ajuda a organizar cobranças, recebimentos, recibos e documentos da locação."
      ],
      "links": [
        {
          "rotulo": "quando o proprietário precisa usar o Carnê-Leão",
          "href": "https://alugando.com/blog/imposto-aluguel-quando-usar-carne-leao/"
        },
        {
          "rotulo": "Alugando.com",
          "href": "https://alugando.com/"
        },
        {
          "rotulo": "Receita Federal: rendimentos",
          "href": "https://www.gov.br/receitafederal/pt-br/assuntos/meu-imposto-de-renda/pagamento/carne-leao/rendimentos"
        },
        {
          "rotulo": "Lei 7.713, artigo 2",
          "href": "https://www.planalto.gov.br/ccivil_03/leis/l7713.htm"
        },
        {
          "rotulo": "Receita Federal: ano",
          "href": "https://www.gov.br/receitafederal/pt-br/assuntos/meu-imposto-de-renda/tabelas/2026"
        },
        {
          "rotulo": "Receita Federal: imobiliaria",
          "href": "https://www.gov.br/receitafederal/pt-br/assuntos/meu-imposto-de-renda/malha-fiscal/antecipacao/rendimentos-recebidos-de-empresa/recebo-alugueis-de-uma-imobiliaria"
        }
      ]
    }
  ],
  "chamada": {
    "titulo": "Organize a rotina do aluguel",
    "texto": "Organize cobranças, recebimentos, recibos e documentos da locação no Alugando.com.",
    "rotulo": "Conhecer o Alugando.com",
    "href": "/#como-funciona"
  }
},
{
  "id": "carne-leao-aluguel",
  "slug": "imposto-aluguel-quando-usar-carne-leao",
  "titulo": "Imposto sobre aluguel: quando o proprietário precisa usar o Carnê-Leão",
  "descricao": "Veja quando o aluguel entra no Carnê-Leão, como tratar repasses da imobiliária e quais regras mensais valem para recebimentos de 2026.",
  "resumo": "Identifique a fonte pagadora, o mês do recebimento e os valores que precisam ser conferidos antes da apuração.",
  "categoria": "Imposto sobre aluguel",
  "etapaFunil": "consideracao",
  "intencao": "Saber quando o proprietário pessoa física residente no Brasil precisa apurar Carnê-Leão sobre aluguel e distinguir IRRF de locatário PJ.",
  "palavrasChave": [
    "imposto sobre aluguel",
    "carnê-leão aluguel"
  ],
  "publicadoEm": "2026-09-09",
  "revisadoEm": "2026-09-09",
  "tempoLeitura": "5 min",
  "autor": {
    "nome": "Equipe editorial Alugando.com",
    "cargo": "Redação editorial"
  },
  "revisor": {
    "nome": "Alugando.com",
    "cargo": "Revisão automatizada de fontes"
  },
  "imagem": {
    "src": "/midia/seo/img_1788805424542_8a257fa8.webp",
    "largura": 1536,
    "altura": 1024,
    "alt": "Casa, documentos e calendário representam a organização mensal do aluguel."
  },
  "imagemInterna": {
    "src": "/midia/seo/img_1788805439982_15958bb0.webp",
    "largura": 1536,
    "altura": 1024,
    "alt": "Pagamento à imobiliária em agosto determina apuração de agosto, mesmo com repasse em setembro.",
    "aposSecao": 4,
    "legenda": "Pagamento à imobiliária em agosto determina apuração de agosto, mesmo com repasse em setembro."
  },
  "secoes": [
    {
      "titulo": "O primeiro passo é identificar quem aluga o imóvel",
      "paragrafos": [
        "Se você é pessoa física residente no Brasil e recebe aluguel de outra pessoa física, esse rendimento entra na apuração mensal do Carnê-Leão. Apurar não significa necessariamente ter imposto a pagar: o resultado depende dos valores, das deduções aplicáveis e das regras do mês.",
        "Este guia trata de recebimentos do ano-calendário de 2026. Ele não cobre o proprietário não residente nem a locação feita por uma empresa proprietária do imóvel."
      ],
      "links": [
        {
          "rotulo": "Receita Federal: Carnê-Leão",
          "href": "https://www.gov.br/receitafederal/pt-br/assuntos/meu-imposto-de-renda/pagamento/carne-leao/carne-leao/"
        },
        {
          "rotulo": "Receita Federal: retenção sobre aluguéis",
          "href": "https://www.gov.br/receitafederal/pt-br/acesso-a-informacao/perguntas-frequentes/sped/efd-reinf/efdr/2-eventos-da-efd-reinf/2-13-9-e-necessario-informar"
        }
      ]
    },
    {
      "titulo": "Inquilino pessoa física ou empresa: os caminhos são diferentes",
      "paragrafos": [
        "Com inquilino pessoa física, o proprietário reúne os recebimentos sujeitos ao Carnê-Leão e faz a apuração mensal. Com inquilino pessoa jurídica pagando aluguel a proprietário pessoa física residente no Brasil, o rendimento está sujeito à retenção do imposto na fonte pela empresa, conforme as regras aplicáveis.",
        "Não lance o mesmo aluguel pago pela empresa como se fosse recebido de pessoa física para recolher Carnê-Leão novamente. Confira a identificação da fonte pagadora, o rendimento e o IRRF no comprovante fornecido pela empresa. Ausência de retenção não transforma automaticamente a empresa em pessoa física."
      ],
      "links": [
        {
          "rotulo": "Receita Federal: retenção sobre aluguéis",
          "href": "https://www.gov.br/receitafederal/pt-br/acesso-a-informacao/perguntas-frequentes/sped/efd-reinf/efdr/2-eventos-da-efd-reinf/2-13-9-e-necessario-informar"
        }
      ]
    },
    {
      "titulo": "Receber pela imobiliária não muda quem é o inquilino",
      "paragrafos": [
        "O CNPJ que aparece no repasse pode ser apenas o da administradora. A Receita orienta que o aluguel de inquilino pessoa física, mesmo administrado por imobiliária, seja informado como rendimento recebido de pessoa física no Carnê-Leão Web.",
        "Antes de preencher, confira o contrato e o demonstrativo. Identifique o locatário e separe o aluguel bruto, os encargos e a taxa de administração. O valor líquido que chegou à sua conta não explica sozinho o tratamento tributário."
      ],
      "links": [
        {
          "rotulo": "Receita Federal: aluguel administrado por imobiliária",
          "href": "https://www.gov.br/receitafederal/pt-br/assuntos/meu-imposto-de-renda/malha-fiscal/antecipacao/rendimentos-recebidos-de-empresa/recebo-alugueis-de-uma-imobiliaria"
        }
      ]
    },
    {
      "titulo": "Qual mês vale: pagamento ou repasse?",
      "paragrafos": [
        "Para aluguel recebido por administradora, vale a data em que o locatário pagou ao proprietário ou à imobiliária. O repasse posterior não desloca o recebimento para outro mês.",
        "Exemplo: o inquilino pessoa física paga à imobiliária em 31 de agosto de 2026, e ela repassa ao proprietário em 3 de setembro. Esse aluguel pertence à apuração de agosto. Se houver imposto devido, o prazo normal é o último dia útil de setembro.",
        "Guarde as duas datas no controle: pagamento do inquilino e repasse ao proprietário. Essa separação permite conferir o mês fiscal sem perder a conciliação bancária."
      ],
      "links": [
        {
          "rotulo": "Receita Federal: rendimentos sujeitos ao Carnê-Leão",
          "href": "https://www.gov.br/receitafederal/pt-br/assuntos/meu-imposto-de-renda/pagamento/carne-leao/rendimentos"
        },
        {
          "rotulo": "Receita Federal: Carnê-Leão",
          "href": "https://www.gov.br/receitafederal/pt-br/assuntos/meu-imposto-de-renda/pagamento/carne-leao/carne-leao/"
        }
      ]
    },
    {
      "titulo": "Quais valores podem sair do aluguel tributável?",
      "paragrafos": [
        "A Receita permite excluir certos encargos do aluguel recebido quando o ônus foi exclusivamente do locador: impostos, taxas e emolumentos incidentes sobre o imóvel; aluguel da sublocação; despesas de cobrança ou recebimento; e condomínio.",
        "Assim, IPTU e comissão de administração podem se enquadrar nessas categorias quando efetivamente suportados pelo proprietário. Guarde os comprovantes e não desconte novamente um encargo custeado pelo inquilino. Uma despesa existir no extrato não basta para torná-la dedutível.",
        "Exemplo apenas de organização: aluguel de R$ 3.000 e comissão de R$ 300 paga pelo locador para recebê-lo resultam em R$ 2.700 após essa exclusão. Isso ainda não é o imposto: faltam reunir os demais rendimentos sujeitos à apuração e aplicar as regras pertinentes."
      ],
      "links": [
        {
          "rotulo": "Receita Federal: rendimentos do capital",
          "href": "https://www.gov.br/receitafederal/pt-br/assuntos/meu-imposto-de-renda/preenchimento/manual-mir/rendimentos/rendimentos-do-capital"
        }
      ]
    },
    {
      "titulo": "O que mudou no cálculo mensal em 2026",
      "paragrafos": [
        "Desde janeiro de 2026, há uma redução do imposto: para rendimentos tributáveis sujeitos à incidência mensal de até R$ 5.000, ela pode zerar o imposto; entre R$ 5.000,01 e R$ 7.350, a redução é parcial e decrescente. Acima de R$ 7.350, essa redução não se aplica.",
        "Esses limites não são uma isenção por imóvel ou por contrato. Reúna os rendimentos que pertencem à mesma apuração. A tabela progressiva continua existindo: a redução é aplicada ao imposto calculado, limitada ao seu valor. Não confunda rendimento tributável usado na redução com a base após todas as deduções.",
        "Salários sujeitos ao IR na fonte não entram no cálculo do Carnê-Leão. Ainda assim, ter mais de uma fonte de renda pode gerar diferença no ajuste anual. Um mês sem Darf não prova, sozinho, que não haverá imposto no ano."
      ],
      "links": [
        {
          "rotulo": "Receita Federal: tributação de 2026",
          "href": "https://www.gov.br/receitafederal/pt-br/assuntos/meu-imposto-de-renda/tabelas/2026"
        },
        {
          "rotulo": "Lei nº 15.270/2025",
          "href": "https://www.planalto.gov.br/ccivil_03/_ato2023-2026/2025/lei/l15270.htm"
        },
        {
          "rotulo": "Receita Federal: Carnê-Leão",
          "href": "https://www.gov.br/receitafederal/pt-br/assuntos/meu-imposto-de-renda/pagamento/carne-leao/carne-leao/"
        }
      ]
    },
    {
      "titulo": "Apuração mensal de 2026 não é a declaração entregue em 2026",
      "paragrafos": [
        "A Lei nº 15.270/2025 distingue os períodos: as regras mensais começam em janeiro de 2026; a redução anual referente a esses rendimentos passa a valer no exercício de 2027, ano-calendário de 2026.",
        "Na rotina, selecione o ano correto no Carnê-Leão Web e organize cada recebimento no mês correspondente. Não espere a declaração anual para resolver uma obrigação mensal já vencida. O pagamento do Carnê-Leão, quando devido, vence no último dia útil do mês seguinte ao recebimento."
      ],
      "links": [
        {
          "rotulo": "Lei nº 15.270/2025",
          "href": "https://www.planalto.gov.br/ccivil_03/_ato2023-2026/2025/lei/l15270.htm"
        },
        {
          "rotulo": "Receita Federal: Carnê-Leão",
          "href": "https://www.gov.br/receitafederal/pt-br/assuntos/meu-imposto-de-renda/pagamento/carne-leao/carne-leao/"
        }
      ]
    },
    {
      "titulo": "Uma conferência curta antes de fechar o mês",
      "paragrafos": [
        "Use esta lista como rotina de organização. Casos com copropriedade, usufruto, residência no exterior ou divergências entre contrato e pagamento exigem conferir o enquadramento específico antes do preenchimento."
      ],
      "itens": [
        "Identifique o proprietário beneficiário e o locatário: pessoa física ou jurídica.",
        "Confira a data do pagamento, inclusive quando a imobiliária recebe primeiro.",
        "Separe os encargos excluíveis e preserve os documentos que demonstram o ônus do locador.",
        "Reúna os rendimentos da apuração e confira o resultado no Carnê-Leão Web do ano correto.",
        "Se houver imposto, confira o Darf e seu vencimento; guarde também o comprovante de pagamento."
      ],
      "links": [
        {
          "rotulo": "Como organizar recebimentos de aluguel mês a mês",
          "href": "/blog/como-organizar-recebimentos-de-aluguel/"
        }
      ]
    },
    {
      "titulo": "Fontes e próximos passos",
      "paragrafos": [
        "Conteúdo conferido em 7 de setembro de 2026, com orientações da Receita Federal e a Lei nº 15.270/2025. Os exemplos mostram organização e enquadramento, sem substituir a apuração da situação completa.",
        "Para manter o histórico pronto para essa conferência, veja também como organizar recebimentos de aluguel mês a mês. Conheça o Alugando.com para organizar recebimentos e comprovantes da locação."
      ],
      "links": []
    }
  ],
  "chamada": {
    "titulo": "Organize a rotina do aluguel",
    "texto": "Mantenha recebimentos e comprovantes ligados à rotina de cada imóvel.",
    "rotulo": "Conhecer o Alugando.com",
    "href": "/#como-funciona"
  }
},
  {
  "id": "pix-recibo-aluguel",
  "slug": "comprovante-pix-substitui-recibo-aluguel",
  "titulo": "Comprovante de Pix substitui recibo de aluguel?",
  "descricao": "Entenda a diferença entre comprovante de Pix e recibo de aluguel, o que registrar na quitação e como organizar pagamentos parciais.",
  "resumo": "O Pix registra a transferência. O recibo explica a que pagamento ela corresponde. Veja como usar os dois documentos para deixar claro o que foi recebido e o que ainda está pendente.",
  "categoria": "Gestão da locação",
  "etapaFunil": "consideracao",
  "intencao": "Entender o valor do comprovante de Pix e emitir recibo discriminado de aluguel sem confundir transferência com quitação ampla.",
  "palavrasChave": [
    "comprovante pix substitui recibo de aluguel",
    "recibo de aluguel",
    "comprovante de pagamento de aluguel"
  ],
  "publicadoEm": "2026-09-06",
  "revisadoEm": "2026-09-06",
  "tempoLeitura": "5 min",
  "autor": {
    "nome": "Alugando.com",
    "cargo": "Conteúdo editorial"
  },
  "revisor": {
    "nome": "Assistente editorial do Alugando.com",
    "cargo": "Revisão automatizada de fontes e clareza"
  },
  "secoes": [
    {
      "titulo": "A resposta curta: guarde o Pix e detalhe o recibo",
      "paragrafos": [
        "O comprovante de um Pix concluído é uma evidência da transferência e pode ajudar a demonstrar o pagamento do aluguel. Mas ele pode não explicar qual imóvel, período ou parcela está sendo pago. Por isso, o caminho mais claro é guardar o comprovante bancário junto de um recibo que discrimine os valores recebidos.",
        "Para as locações urbanas abrangidas pela Lei do Inquilinato, o artigo 22, inciso VI, determina que o locador forneça recibo discriminado das quantias pagas pelo locatário e proíbe a quitação genérica. O meio usado para transferir o dinheiro não elimina esse dever.",
        "Também não cabe dizer que um Pix nunca serve como prova de pagamento. O parágrafo único do artigo 320 do Código Civil admite a quitação quando o pagamento resulta do texto ou das circunstâncias, mesmo sem todos os requisitos formais. A suficiência da prova depende do caso. Este guia orienta a organização dos documentos; não decide uma disputa jurídica."
      ],
      "links": [
        {
          "rotulo": "Lei do Inquilinato, artigo 22, VI",
          "href": "https://www.planalto.gov.br/ccivil_03/leis/l8245compilado.htm"
        },
        {
          "rotulo": "Código Civil, artigos 319 e 320",
          "href": "https://www.planalto.gov.br/ccivil_03/leis/2002/l10406compilada.htm"
        }
      ]
    },
    {
      "titulo": "O que cada documento ajuda a conferir",
      "paragrafos": [
        "No comprovante bancário, confira o status da operação, a data, o valor e quem enviou e recebeu o dinheiro. Confira também o crédito no extrato de quem recebe. Um agendamento ou uma captura de tela enviada na conversa não deve ser tratado, sozinho, como confirmação do recebimento.",
        "No recibo, descreva a obrigação à qual o valor foi associado: aluguel de qual imóvel, referente a qual período, com quais componentes e em que valor. Essa ligação evita depender da memória quando um mesmo pagador transfere dinheiro por mais de um imóvel ou acerta uma pendência antiga."
      ],
      "itens": [
        "Comprovante de Pix: registre a transferência e preserve o arquivo original.",
        "Recibo: identifique o pagamento reconhecido pelo locador ou por seu representante.",
        "Controle mensal: ligue os dois documentos ao imóvel e ao período correspondentes."
      ]
    },
    {
      "titulo": "O que colocar no recibo de aluguel",
      "paragrafos": [
        "O artigo 320 do Código Civil indica os elementos da quitação: valor e espécie da dívida, identificação do devedor ou de quem pagou por ele, tempo e lugar do pagamento e assinatura do credor ou de seu representante.",
        "Na rotina da locação, vale acrescentar informações que tornem essa identificação mais simples. Endereço do imóvel, período de referência e separação de componentes são orientações práticas para deixar o registro claro; não são uma transcrição literal de todos os requisitos do Código Civil."
      ],
      "itens": [
        "Nome do locador ou representante que reconhece o recebimento e do locatário; identifique quem pagou se for outra pessoa.",
        "Imóvel e período a que o pagamento se refere.",
        "Valor recebido e discriminação do aluguel e de outros componentes efetivamente incluídos.",
        "Data e lugar do pagamento, com referência ao Pix correspondente, quando houver.",
        "Indicação de pagamento integral ou parcial e do saldo que permanece em aberto, se aplicável.",
        "Assinatura do credor ou representante. A emissão de um PDF, por si só, não deve ser confundida com uma assinatura."
      ],
      "links": [
        {
          "rotulo": "Conferir os requisitos no artigo 320 do Código Civil",
          "href": "https://www.planalto.gov.br/ccivil_03/leis/2002/l10406compilada.htm"
        }
      ]
    },
    {
      "titulo": "Exemplo: entrou R$ 1.500 de um aluguel de R$ 2.000",
      "paragrafos": [
        "Imagine um aluguel de R$ 2.000 referente a setembro de 2026. As partes combinaram um pagamento parcial, e o locatário transferiu R$ 1.500. O recibo deve refletir o que efetivamente foi recebido, sem apresentar o mês inteiro como quitado.",
        "Exemplo de descrição a adaptar: “Recebidos R$ 1.500 em 6 de setembro de 2026, por Pix, como pagamento parcial do aluguel referente a setembro de 2026 do imóvel [identificação]. Do aluguel de R$ 2.000 deste período, permanece saldo de R$ 500.” Complete a identificação das partes e os demais elementos do recibo.",
        "Esse exemplo supõe apenas o aluguel, sem multa, juros ou outros encargos. Se houver componentes adicionais, registre-os separadamente conforme o caso e o contrato. Não use uma frase de quitação total quando a situação ainda tiver saldo a conferir."
      ]
    },
    {
      "titulo": "Três situações que merecem uma observação no registro",
      "paragrafos": [
        "Quando outra pessoa faz o Pix pelo locatário, registre quem transferiu e a quem o pagamento se refere. Se o valor chegar sem descrição, confirme a referência antes de associá-lo a um mês.",
        "Quando uma transferência cobre vários períodos ou imóveis, detalhe a distribuição no recibo e no controle. E, se houver diferença entre o que foi combinado e o que entrou, mantenha a pendência explícita até esclarecer o valor."
      ],
      "itens": [
        "Pagamento por terceiro: vincule o pagador ao locatário e ao imóvel.",
        "Uma transferência para várias parcelas: discrimine a parcela de cada período.",
        "Valor divergente: registre o recebido e a diferença, sem presumir que houve desconto ou quitação."
      ]
    },
    {
      "titulo": "Uma rotina curta para não perder os comprovantes",
      "paragrafos": [
        "Ao receber o pagamento, confira o crédito, identifique o imóvel e o período e registre o valor. Depois, prepare o recibo, confira os dados e guarde sua versão final junto do comprovante bancário. Use um nome de arquivo que ajude a localizar o mês e o imóvel, sem expor dados pessoais desnecessários.",
        "No fechamento, revise apenas o que ainda está sem identificação, com diferença de valor ou sem documento. A meta é conseguir responder rapidamente: o que entrou, a que se refere e o que falta resolver."
      ],
      "links": [
        {
          "rotulo": "Leia também: como organizar recebimentos de aluguel mês a mês",
          "href": "/blog/como-organizar-recebimentos-de-aluguel/"
        }
      ]
    },
    {
      "titulo": "Fontes e data da consulta",
      "paragrafos": [
        "Este guia foi conferido em 6 de setembro de 2026 com os textos compilados da Lei nº 8.245/1991 e do Código Civil. O assunto é documentação de pagamentos de locação; não há cálculo nem orientação tributária para um ano fiscal. Exemplos e checklists são sugestões de organização e devem ser adaptados ao contrato e à situação concreta."
      ],
      "links": [
        {
          "rotulo": "Lei nº 8.245/1991 — texto compilado oficial",
          "href": "https://www.planalto.gov.br/ccivil_03/leis/l8245compilado.htm"
        },
        {
          "rotulo": "Lei nº 10.406/2002 — Código Civil compilado",
          "href": "https://www.planalto.gov.br/ccivil_03/leis/2002/l10406compilada.htm"
        }
      ]
    }
  ],
  "chamada": {
    "titulo": "Deixe cada pagamento ligado ao seu imóvel",
    "texto": "Conheça o Alugando.com para organizar recebimentos e comprovantes na rotina da locação.",
    "rotulo": "Conhecer o Alugando.com",
    "href": "/#como-funciona"
  },
  "imagem": {
    "src": "https://alugando.com/midia/seo/img_1788714820637_84faee13.webp",
    "largura": 1536,
    "altura": 1024,
    "alt": "Comprovante bancário e recibo relacionados ao mesmo imóvel"
  },
  "imagemInterna": {
    "src": "https://alugando.com/midia/seo/img_1788714832326_d0c55596.webp",
    "largura": 1536,
    "altura": 1024,
    "alt": "Quatro parcelas iguais: três recebidas e uma pendente, ilustrando R$ 1.500 de um aluguel de R$ 2.000",
    "aposSecao": 4,
    "legenda": "Exemplo: três quartos do aluguel recebidos e um quarto ainda pendente."
  }
},
  {
    id: "organizar-recebimentos-aluguel",
    slug: "como-organizar-recebimentos-de-aluguel",
    titulo: "Como organizar recebimentos de aluguel mês a mês",
    descricao: "Um método simples para acompanhar vencimentos, pagamentos, comprovantes e pendências de cada imóvel sem perder o histórico.",
    resumo: "Organize cada recebimento em uma rotina mensal que mostra quem pagou, o que falta conferir e quais documentos precisam ficar guardados.",
    categoria: "Gestão da locação",
    etapaFunil: "consideracao",
    intencao: "Ajudar o locador a escolher um processo confiável para controlar recebimentos",
    palavrasChave: ["controle de aluguel", "recebimentos de aluguel", "organização de imóveis"],
    publicadoEm: "2026-09-04",
    revisadoEm: "2026-09-04",
    tempoLeitura: "6 min",
    autor: { nome: "Tiago Silva Loureiro", cargo: "Fundador do Alugando.com" },
    revisor: { nome: "Tiago Silva Loureiro", cargo: "Revisão de produto" },
    imagem: {
      src: "/blog/como-organizar-recebimentos-de-aluguel-capa.svg",
      largura: 1536,
      altura: 1024,
      alt: "Casas e comprovantes organizados em uma visão mensal",
    },
    secoes: [
      {
        titulo: "O controle começa antes do dinheiro cair",
        paragrafos: [
          "Uma boa rotina não começa procurando comprovantes no fim do mês. Ela começa com uma lista clara dos imóveis, de quem paga cada aluguel, do valor esperado e do dia habitual de recebimento.",
          "Essa lista funciona como o esperado do mês. Quando um pagamento chega, você compara o realizado com o esperado e registra a diferença enquanto ainda lembra do contexto.",
        ],
        itens: [
          "Identifique cada imóvel por um nome fácil de reconhecer.",
          "Registre o locatário e o dia habitual do pagamento.",
          "Separe o valor do aluguel de condomínio, IPTU e outros componentes.",
          "Anote quem administra a locação e se existe taxa de administração.",
        ],
      },
      {
        titulo: "Use um fechamento mensal, não uma lista infinita",
        paragrafos: [
          "Controle por imóvel é útil, mas o fechamento precisa ser por mês. Assim você consegue responder duas perguntas objetivas: todos os recebimentos esperados chegaram e todos os comprovantes daquele período estão guardados?",
          "Um mês deve permanecer aberto enquanto houver pagamento sem identificação, valor divergente ou documento faltando. Fechar cedo demais transforma uma dúvida visível em um erro escondido.",
        ],
        itens: [
          "Aguardando: o vencimento ainda não chegou ou o pagamento não apareceu.",
          "Recebido: o valor chegou e foi associado ao imóvel correto.",
          "Pendente: existe uma diferença ou informação que precisa ser confirmada.",
          "Fechado: o mês foi revisado e não depende mais de memória.",
        ],
      },
      {
        titulo: "Guarde a evidência junto do recebimento",
        links: [{ rotulo: "Comprovante de Pix substitui recibo de aluguel?", href: "/blog/comprovante-pix-substitui-recibo-aluguel/" }],
        paragrafos: [
          "Prints, comprovantes de Pix e demonstrativos de imobiliária perdem valor quando ficam espalhados entre conversas, downloads e pastas sem nome. O documento deve ficar ligado ao imóvel, ao pagamento e ao mês correspondente.",
          "Use nomes previsíveis e evite editar o arquivo original. Se precisar fazer uma observação, registre-a separadamente para preservar o documento recebido.",
        ],
        itens: [
          "Data em que o valor entrou.",
          "Imóvel e pagador associados.",
          "Valor efetivamente recebido.",
          "Origem do comprovante.",
          "Pendência ou divergência identificada.",
        ],
      },
      {
        titulo: "Planilha ajuda, mas exige disciplina",
        paragrafos: [
          "Uma planilha pode funcionar para poucos imóveis quando uma pessoa atualiza tudo com frequência. O problema aparece quando o comprovante chega por outro canal, a atualização fica para depois ou a estrutura muda de um mês para o outro.",
          "Um sistema reduz esse trabalho ao reunir cadastro, recebimentos, documentos e fechamento no mesmo fluxo. A escolha não depende apenas da quantidade de imóveis. Depende de quanto do processo ainda fica guardado na cabeça de alguém.",
        ],
      },
      {
        titulo: "Transforme o fechamento em uma rotina curta",
        paragrafos: [
          "Reserve um momento fixo para olhar apenas as exceções. Pagamentos identificados e valores esperados não precisam ser conferidos do zero. A atenção deve ir para atrasos, diferenças e documentos ausentes.",
          "Quando o processo mostra o que falta, o fechamento deixa de ser uma busca e vira uma decisão. Você confirma o que aconteceu no mês e segue com um histórico que pode ser consultado depois.",
        ],
      },
    ],
    chamada: {
      titulo: "Recebimentos, comprovantes e imposto no mesmo fluxo",
      texto: "O Alugando.com acompanha quem pagou, organiza os documentos e prepara o fechamento mensal. O primeiro mês é grátis.",
      rotulo: "Conhecer o Alugando.com",
      href: "/#como-funciona",
    },
  },
];

export function artigoPorSlug(slug: string): ArtigoBlog | undefined {
  return ARTIGOS_BLOG.find((artigo) => artigo.slug === slug);
}
